import dayjs, { Dayjs } from "dayjs";
import { EventCreate } from "src/store/api/generatedApi";

export const PROXIMITY_THRESHOLD_SECONDS = 10;

/**
 * Finds the nearest candidate event to the current playback position.
 * Priority: candidates containing the timestamp (latest start wins),
 * then nearest by distance within threshold.
 */
export const findNearestCandidate = (
  candidateEvents: EventCreate[],
  currentTimestamp: Dayjs,
  thresholdSeconds = PROXIMITY_THRESHOLD_SECONDS
): EventCreate | null => {
  if (!candidateEvents.length) return null;

  // First priority: find candidates that contain the current timestamp
  // If multiple, take the one with the latest start time
  let containingCandidate: EventCreate | null = null;
  let latestStart: Dayjs | null = null;

  for (const candidate of candidateEvents) {
    const candidateStart = dayjs(candidate.event.start);
    const candidateEnd = dayjs(candidate.event.end);

    const isWithin =
      currentTimestamp.isAfter(candidateStart) &&
      currentTimestamp.isBefore(candidateEnd);

    if (isWithin) {
      if (!latestStart || candidateStart.isAfter(latestStart)) {
        containingCandidate = candidate;
        latestStart = candidateStart;
      }
    }
  }

  if (containingCandidate) {
    return containingCandidate;
  }

  // Fallback: find nearest by distance within threshold
  let nearest: EventCreate | null = null;
  let nearestDistance = Infinity;

  for (const candidate of candidateEvents) {
    const candidateStart = dayjs(candidate.event.start);
    const candidateEnd = dayjs(candidate.event.end);
    const startDistance = Math.abs(
      candidateStart.diff(currentTimestamp, "second")
    );
    const endDistance = Math.abs(candidateEnd.diff(currentTimestamp, "second"));
    const distance = Math.min(startDistance, endDistance);
    if (distance < nearestDistance && distance < thresholdSeconds) {
      nearest = candidate;
      nearestDistance = distance;
    }
  }

  return nearest;
};
